import React from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleIcon from "@mui/icons-material/People";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import RepeatIcon from "@mui/icons-material/Repeat";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewModuleIcon from "@mui/icons-material/ViewModule";

type FilterOption = { value: string; label: string };

type FilterConfig = {
  key: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  minWidth?: number;
};

type SharedToggle = {
  value: boolean;
  onToggle: () => void;
  tooltip?: string;
};

type ExpandToggle = {
  value: boolean;
  onToggle: () => void;
  tooltip?: string;
};

type GroupingToggle = {
  value: "time" | "recurrence";
  onChange: (value: "time" | "recurrence") => void;
};

// Pulls fresh data from an upstream system on demand, for pages whose records
// are mirrored from somewhere else.
type RefreshAction = {
  onRefresh: () => void;
  busy?: boolean;
  tooltip?: string;
};

type ViewToggle = {
  value: "table" | "card";
  onChange: (value: "table" | "card") => void;
};

interface SearchFilterBarProps {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onSearchApply: () => void;
  searchPlaceholder?: string;
  filters?: FilterConfig[];
  sharedToggle?: SharedToggle;
  expandToggle?: ExpandToggle;
  refreshAction?: RefreshAction;
  groupingToggle?: GroupingToggle;
  viewToggle?: ViewToggle;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  searchQuery,
  onSearchQueryChange,
  onSearchApply,
  searchPlaceholder = "Search",
  filters = [],
  sharedToggle,
  expandToggle,
  refreshAction,
  groupingToggle,
  viewToggle,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const showIcons =
    Boolean(sharedToggle) ||
    Boolean(expandToggle) ||
    Boolean(refreshAction) ||
    Boolean(groupingToggle) ||
    Boolean(viewToggle);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        overflowX: "auto",
        overflowY: "visible",
        pb: 0.5,
        pt: 0.75,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "nowrap",
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        <TextField
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          placeholder={searchPlaceholder}
          size="small"
          sx={{ minWidth: 200 }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              onSearchApply();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Search" arrow>
                  <IconButton size="small" onClick={onSearchApply} edge="end">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        {filters.map((filter) => (
          <FormControl
            key={filter.key}
            size="small"
            sx={{ minWidth: filter.minWidth || 140 }}
          >
            <InputLabel>{filter.label}</InputLabel>
            <Select
              label={filter.label}
              value={filter.value}
              onChange={(event) =>
                filter.onChange(event.target.value as string)
              }
            >
              {filter.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
      </Box>

      {showIcons && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            ml: "auto",
            flex: "0 0 auto",
          }}
        >
          {refreshAction && (
            <Tooltip
              title={refreshAction.tooltip || "Refresh from source"}
              arrow
            >
              <IconButton
                onClick={refreshAction.onRefresh}
                disabled={refreshAction.busy}
                sx={{
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                  color: isDarkMode ? "#a0aec0" : "#4a5568",
                  border: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                  "& .MuiSvgIcon-root": refreshAction.busy
                    ? { animation: "spin 1s linear infinite" }
                    : undefined,
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          {sharedToggle && (
            <Tooltip title={sharedToggle.tooltip || "Shared Only"} arrow>
              <IconButton
                onClick={sharedToggle.onToggle}
                sx={{
                  backgroundColor: sharedToggle.value
                    ? isDarkMode
                      ? "#3182CE"
                      : "#3182CE"
                    : isDarkMode
                      ? "#2d2d2d"
                      : "#f7fafc",
                  color: sharedToggle.value
                    ? "white"
                    : isDarkMode
                      ? "#a0aec0"
                      : "#4a5568",
                  border: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                  borderRadius: "4px",
                  width: 40,
                  height: 40,
                  "&:hover": {
                    backgroundColor: sharedToggle.value
                      ? "#2B6CB0"
                      : isDarkMode
                        ? "#333"
                        : "#e2e8f0",
                  },
                }}
              >
                <PeopleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {expandToggle && (
            <Tooltip
              title={
                expandToggle.tooltip ||
                (expandToggle.value ? "Collapse All" : "Expand All")
              }
              arrow
            >
              <IconButton
                onClick={expandToggle.onToggle}
                sx={{
                  backgroundColor: expandToggle.value
                    ? isDarkMode
                      ? "#805AD5"
                      : "#805AD5"
                    : isDarkMode
                      ? "#2d2d2d"
                      : "#f7fafc",
                  color: expandToggle.value
                    ? "white"
                    : isDarkMode
                      ? "#a0aec0"
                      : "#4a5568",
                  border: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                  borderRadius: "4px",
                  width: 40,
                  height: 40,
                  "&:hover": {
                    backgroundColor: expandToggle.value
                      ? "#6B46C1"
                      : isDarkMode
                        ? "#333"
                        : "#e2e8f0",
                  },
                }}
              >
                {expandToggle.value ? (
                  <UnfoldLessIcon fontSize="small" />
                ) : (
                  <UnfoldMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}

          {groupingToggle && (
            <ToggleButtonGroup
              value={groupingToggle.value}
              exclusive
              onChange={(_, newMode) =>
                newMode && groupingToggle.onChange(newMode)
              }
              size="small"
              sx={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                height: 40,
                "& .MuiToggleButton-root": {
                  color: isDarkMode ? "#a0aec0" : "#4a5568",
                  borderColor: isDarkMode ? "#333" : "#e2e8f0",
                  "&.Mui-selected": {
                    backgroundColor: isDarkMode ? "#38B2AC" : "#38B2AC",
                    color: "#fff",
                  },
                },
              }}
            >
              <ToggleButton value="time">
                <Tooltip title="Group by Time Period" arrow>
                  <CalendarViewMonthIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="recurrence">
                <Tooltip title="Group by Recurrence" arrow>
                  <RepeatIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {viewToggle && (
            <ToggleButtonGroup
              value={viewToggle.value}
              exclusive
              onChange={(_, newMode) => newMode && viewToggle.onChange(newMode)}
              size="small"
              sx={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                height: 40,
                "& .MuiToggleButton-root": {
                  color: isDarkMode ? "#a0aec0" : "#4a5568",
                  borderColor: isDarkMode ? "#333" : "#e2e8f0",
                  "&.Mui-selected": {
                    backgroundColor: isDarkMode ? "#1a1a1a" : "#e2e8f0",
                    color: isDarkMode ? "#fff" : "#1a202c",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <Tooltip title="Table View" arrow>
                  <TableRowsIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="card">
                <Tooltip title="Card View" arrow>
                  <ViewModuleIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchFilterBar;
